import './CreateMap.scss';
import { useApp } from '@react';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { useMutation } from 'react-query';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Dropzone, Icon, Keywords, Loading, Text, toast } from '../../../components';
import { Api } from '../../../services';

async function createMap(data) {
  return Api.post('v2/maps', data);
}

// Helper to determine Azure Maps domain directly from host context
const getAzureMapsDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isGov =
    hostname.endsWith('.gov') || hostname.includes('azure-gov') || hostname.includes('usgov');
  return isGov ? 'atlas.azure.us' : 'atlas.microsoft.com';
};

function CreateMap() {
  const app = useApp();
  const history = useHistory();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [keywords, setKeywords] = useState<any>([]);
  const [isGeographic, setIsGeographic] = useState<boolean>(false);
  const [floorNumber, setFloorNumber] = useState<number | string>(1);
  const [floorError, setFloorError] = useState<string>('');
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cache, setCache] = useState({});
  const [query, setQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLocation, setIsLocation] = useState<boolean>(false);
  const [coordVal, setCoordVal] = useState<string>('');
  const [mapKey, setMapKey] = useState<string | null>(null);

  const isDisabled = !name || files.length === 0 || !!floorError;
  const createMapMutation = useMutation(createMap);
  const SwalInstructions = withReactContent(Swal);
  const typeaheadRef = useRef(null);

  // Fetch Azure Maps Key directly from API
  useEffect(() => {
    Api.get(`/me/map-server-key`)
      .then((resp) => {
        setMapKey(resp.data as string);
      })
      .catch((err) => {
        console.error('Failed to fetch map key:', err);
      });
  }, []);

  useEffect(() => {
    if (!isGeographic) {
      setLatitude(0);
      setLongitude(0);
      setOptions([]);
      setCache({});
    }
  }, [isGeographic]);

  const showInstructionsPopup = () => {
    SwalInstructions.fire({
      allowOutsideClick: false,
      title: `Important!`,
      html: `<Text>We recommend properly scaling and rotating the Map's overlay image first. Once Places, Gateways, and Stationary Assets are added, they will not automatically move if the size or orientation of the Map's overlay image is changed.<Text/>`,
      icon: 'warning',
      confirmButtonText: 'Okay',
    });
  };

  const handleValidation = () => {
    setIsSubmitted(true);
    if (name && files.length > 0) {
      if (isGeographic && latitude && longitude) {
        handleSubmit();
      } else if (!isGeographic) {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const mapData = [
      { key: 'applicationId', value: app.id },
      { key: 'name', value: name },
      { key: 'description', value: description },
      { key: 'isGeographic', value: isGeographic },
      { key: 'latitude', value: latitude },
      { key: 'longitude', value: longitude },
      { key: 'floorNumber', value: floorNumber },
    ];

    const formData = new FormData();
    keywords.forEach((item) => mapData.push({ key: 'keywords', value: item }));
    mapData.forEach((item) => formData.append(item.key, (item as any).value));
    files.forEach((file) => formData.append('image', file));

    try {
      await createMapMutation.mutateAsync(formData, {
        onSuccess: async (data) => {
          setLoading(false);
          toast.success('Successfully created Map.');
          history.push(`/maps/` + data.headers['location'].split('/').slice(-1));
          if (isGeographic) showInstructionsPopup();
        },
      });
    } catch (error) {
      const errors = error?.response?.data?.errors;
      let errorMessage = 'Failed to create map. Please try again.';

      if (errors?.floorNumber?.length > 0) {
        errorMessage = 'Floor number is not valid.';
      }

      toast.error(errorMessage);
      setFiles([]);
      setIsSubmitted(false);
      setFloorNumber(1);
      setIsGeographic(false);
      setIsLocation(false);
      setLoading(false);
    }
  };

  // Direct Azure Search API execution
  const getResults = async (url: string) => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Azure search failed with status ${response.status}`);
    }

    const data = await response.json();
    const azMapsDomain = getAzureMapsDomain();

    const next = `https://${azMapsDomain}/search/poi/json?subscription-key=${mapKey}&api-version=1.0&query=${
      data.summary.query
    }&limit=15&ofs=${data.summary.offset + 10}`;

    const total = data.summary.totalResults;

    const newItems = data.results.map((item: any) => ({
      name: item.address.freeformAddress,
      country: item.address.country,
      countrySubdivision: item.address.countrySubdivisionName,
      position: item.position,
    }));

    return { newItems, next, total };
  };

  const handleSearch = (value) => {
    setQuery(value);

    if (cache[value]) {
      setOptions(cache[value].items);
    } else if (mapKey) {
      const azMapsDomain = getAzureMapsDomain();
      const initCall = `https://${azMapsDomain}/search/address/json?subscription-key=${mapKey}&api-version=1.0&query=${encodeURIComponent(
        value,
      )}&limit=15&ofs=0`;

      setIsLoading(true);
      getResults(initCall)
        .then((resp) => {
          setOptions(resp.newItems);
          setCache({
            ...cache,
            [value]: { items: resp.newItems, next: resp.next, total: resp.total },
          });
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Location search failed:', err);
          setIsLoading(false);
        });
    }
  };

  const handlePagination = () => {
    if (cache[query] && cache[query].items.length !== cache[query].total) {
      setIsLoading(true);
      getResults(cache[query].next)
        .then((resp) => {
          const items = cache[query].items.concat(resp.newItems);
          setOptions(items);
          setCache({ ...cache, [query]: { items: items, next: resp.next, total: resp.total } });
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Pagination fetch failed:', err);
          setIsLoading(false);
        });
    }
  };

  const handleLocation = (selected) => {
    if (selected.length > 0) {
      setLatitude(selected[0].position.lat);
      setLongitude(selected[0].position.lon);
    } else {
      setLatitude(null);
      setLongitude(null);
    }
  };

  const handleLatLng = (value) => {
    let lat = parseFloat(value.split(/[ ,]+/)[0]);
    let lng = parseFloat(value.split(/[ ,]+/)[1]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setLatitude(lat);
      setLongitude(lng);
    } else {
      setLatitude(null);
      setLongitude(null);
    }
  };

  const onDrop = (files) => {
    setFiles(files);
  };

  const isNavigationKey = (key) =>
    ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(key);

  const isValidFloorInput = (value: string): boolean => {
    return /^\d+$/.test(value) && (value === '0' || !value.startsWith('0'));
  };

  const handleFloorInputChange = (e) => {
    const raw = e.target.value;

    if (raw === '') {
      setFloorNumber('');
      setFloorError('');
      return;
    }

    if (!isValidFloorInput(raw)) return;

    const num = parseInt(raw, 10);

    if (num < 1 || num > 999) {
      setFloorError('Floor must be between 1 and 999.');
    } else {
      setFloorError('');
    }

    setFloorNumber(num);
  };

  const preventLeadingZero = (e) => {
    const { value } = e.currentTarget;
    const key = e.key;

    if (isNavigationKey(key)) return;

    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }

    if (value === '' && key === '0') return;

    if (value === '0') {
      e.preventDefault();
      return;
    }

    if (value.length === 1 && value.startsWith('0')) {
      e.preventDefault();
      return;
    }
  };

  const renderTooltip = (tip) => (
    <Tooltip className="mytooltip" id="button-tooltip">
      {tip}
    </Tooltip>
  );

  if (loading) return <Loading />;

  return (
    <Card className="h-100" style={{ overflowY: 'auto' }}>
      <Card.Header className="d-flex flex-row justify-content-left align-items-center">
        <div
          className="icon-wrap"
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'var(--primary)',
            borderRadius: '60px',
            padding: '0.5em 0.5em',
            textAlign: 'right',
          }}
        >
          <i
            className={`sonarIcon sonarIcon-map`}
            style={{ color: 'var(--white)', cursor: 'auto', fontWeight: 'normal' }}
          />
        </div>{' '}
        <Text className="fs-lg fw-semibold" style={{ color: 'var(--dark)' }}>
          &nbsp; Create New Map
        </Text>
      </Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-3" controlId="name">
            <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
              Name
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              isInvalid={isSubmitted && !name}
              placeholder="Enter map name"
              onChange={(e) => setName(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">A name is required</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="description">
            <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
              Description
            </Form.Label>
            <Form.Control
              as="textarea"
              value={description}
              placeholder="Enter map description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <div className="mb-3 d-flex justify-content-around align-items-center">
            <Form.Group>
              <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                Keywords
              </Form.Label>
              <Keywords isAddingKeyword onChange={(keywords) => setKeywords(keywords)} />
            </Form.Group>
            <Form.Group>
              <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                Floor
              </Form.Label>
              <Form.Control
                aria-label="Floor Number"
                type="text"
                isInvalid={!!floorError}
                inputMode="numeric"
                value={floorNumber === '' ? '' : floorNumber.toString()}
                onChange={handleFloorInputChange}
                onKeyDown={preventLeadingZero}
              />
              <Form.Control.Feedback className="position-fixed" type="invalid">
                {floorError}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <div className="d-flex align-items-center">
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip(
                    'Turning on Geospatial will allow you to place your map at its real world coordinates.',
                  )}
                >
                  <span className="d-inline-block">
                    <div className="icon-wrap">
                      <Icon
                        name="info"
                        className="dark-icon mb-2"
                        variant="light"
                        size="md"
                        disabled={false}
                        showPointer={false}
                      />
                    </div>
                  </span>
                </OverlayTrigger>
                <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                  Geospatial
                </Form.Label>
              </div>
              <Form.Check
                className="fs-lg"
                type="switch"
                id="custom-switch"
                value={isGeographic.toString()}
                onChange={(e) => {
                  setIsGeographic(Boolean(e.target.checked));
                }}
              />
            </Form.Group>
          </div>
          {isGeographic && (
            <>
              <div className="d-flex flex-row justify-content-around mb-3">
                <div className="w-100 d-flex flex-column align-items-center">
                  <Form.Group className="mb-3 w-75" controlId="name">
                    <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                      Location
                    </Form.Label>
                    <AsyncTypeahead
                      ref={typeaheadRef}
                      delay={500}
                      disabled={isLocation}
                      id="identifier"
                      isInvalid={isSubmitted && !isLocation && !latitude && !longitude}
                      isLoading={isLoading}
                      inputProps={{ style: { width: '100%' } }}
                      onPaginate={handlePagination}
                      maxResults={10}
                      minLength={1}
                      filterBy={() => true}
                      options={
                        options &&
                        options.map((option) => {
                          return {
                            label: option.name,
                            country: option.country,
                            countrySubdivision: option.countrySubdivision,
                            position: option.position,
                          };
                        })
                      }
                      onSearch={handleSearch}
                      onChange={handleLocation}
                      paginate
                      placeholder="Search for a location "
                      renderMenuItemChildren={(option, props) => (
                        <div title={option.label}>
                          <Highlighter search={props.text}>{option.label}</Highlighter>
                          <div>
                            <small className="filter-sm-name">{option.country}</small>
                            <small className="filter-sm-name"> - {option.countrySubdivision}</small>
                          </div>
                        </div>
                      )}
                      useCache={false}
                    />
                  </Form.Group>
                </div>
                <div>
                  <Form.Group className="mb-3" controlId="name">
                    <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                      {' '}
                    </Form.Label>
                    <Form.Check
                      className="fs-lg"
                      type="switch"
                      id="custom-switch"
                      value={isLocation.toString()}
                      onChange={(e) => {
                        setLatitude(null);
                        setLongitude(null);
                        typeaheadRef.current.clear();
                        setCoordVal('');
                        setIsLocation(Boolean(e.target.checked));
                      }}
                    />
                  </Form.Group>
                </div>
                <div className="w-100 d-flex flex-column align-items-center">
                  <Form.Group className="mb-3 w-75" controlId="name">
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={renderTooltip(
                        <p align="left">
                          Example Coordinates:
                          <br /> 32.97337838454297, -96.72442704829069
                          <br />
                          <br />
                          <ul>
                            <li>Do not use degree symbols or cardinal direction letters</li>
                            <li>Ensure a comma or space is between the coordinates</li>
                            <li>For more tips, see our Help Center article</li>
                          </ul>
                        </p>,
                      )}
                    >
                      <span className="d-inline-block info-icon-span me-2">
                        <div className="info-icon-div">
                          <Icon
                            className="align-sub ignore-click"
                            name="info"
                            variant="dark"
                            disabled={false}
                            size="md"
                          />
                        </div>
                      </span>
                    </OverlayTrigger>
                    <Form.Label className="fs-md fw-semibold" style={{ color: 'var(--dark)' }}>
                      Coordinates
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={coordVal}
                      isInvalid={isSubmitted && isLocation && !latitude && !longitude}
                      placeholder="Enter valid GPS coordinates"
                      disabled={!isLocation}
                      onChange={(e) => {
                        setCoordVal(e.target.value);
                        handleLatLng(e.target.value);
                      }}
                    />
                  </Form.Group>
                  {isLocation && (
                    <a
                      className="fw-semibold gps-guide-link"
                      href="https://thinaer.io/help-center/maps/creating-coordinates/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Guide: Locate & input coordinates
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
          <Form.Group className="mb-3">
            <div className={isSubmitted && !files.length ? 'is-invalid' : ''}>
              <Dropzone className="mb-3" accept="image/*" multiple onDrop={onDrop} />
            </div>
            {isSubmitted && !files.length && (
              <div className="d-flex justify-content-center">
                <div style={{ color: 'red' }}>An image overlay is required</div>
              </div>
            )}
          </Form.Group>
          <div className="w-100 d-flex justify-content-center">
            <Button variant="primary" disabled={isDisabled} onClick={handleValidation}>
              Submit
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

const mapStateToProps = ({ app }) => ({
  app: app.app,
});

export default connect(mapStateToProps)(CreateMap);
